import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  List,
  Button,
  Icon,
  Input,
  Dropdown,
  Popup,
  Message,
  Modal,
} from 'semantic-ui-react';
import { NAME_MAX_LENGTH } from 'src/app.constants';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWithAuth, postWithAuth } from 'src/api';
import {
  ExportProject,
  getExportReportStatus,
  GoogleEarthExport,
  ReportMenuContext,
} from '../mainframe/ExportMenu';
import { downloadReport, exportProject } from '../mainframe/reportHelpers';
import linkPlannerIcon from '../../../src/images/linkplanner-coloured.svg';
import ShareDialog from './ShareDialog';
import messages from 'src/messages';
import {
  loadProject,
  longTaskComplete,
  longTaskStarted,
} from '../mainframe/mainframe.reducer';
import { runWithConfirmation } from 'src/utils/useful_functions';
import { useSelector } from 'react-redux';

export const PROJECTS_QUERY_KEY = ['projectList'];

function ExportSubmenu(props) {
  const { projectId, projectName, isOpen } = props;

  const query = useQuery(
    ['ReportMenu', projectId],
    async () => {
      return await getWithAuth(`project/${projectId}/reports/available`);
    },
    { retry: false, enabled: isOpen }
  );

  if (isOpen && query.isLoading) {
    return (
      <>
        <Dropdown.Item
          icon={{ name: 'spinner', loading: true }}
          text="Loading..."
        />
        <Dropdown.Item
          icon={{ name: 'spinner', loading: true }}
          text="Loading..."
        />
      </>
    );
  }

  const availableReports = query.data;

  return (
    <>
      <ExportProject
        projectId={projectId}
        projectName={projectName}
        report={getExportReportStatus(availableReports, 'lpp')}
      />
      <GoogleEarthExport
        projectId={projectId}
        projectName={projectName}
        report={getExportReportStatus(availableReports, 'kmz')}
      />
    </>
  );
}

const ProjectIcon = ({
  readOnly,
  readWrite,
  admin,
  permissionName,
  modified,
  in_work,
}) => {
  const { formatMessage } = useIntl();

  if (readOnly) {
    return (
      <Popup
        size="tiny"
        trigger={
          <Icon.Group>
            <Icon name="file outline" />
            <Icon corner="top right" name="protect" />
          </Icon.Group>
        }
        content={formatMessage(messages[permissionName])}
      />
    );
  }

  if (in_work && modified) {
    if (admin) {
      // admin / modified
      return (
        <Popup
          size="tiny"
          trigger={
            <Icon
              name="file outline"
              color="red"
              style={{
                display: 'inline',
              }}
            />
          }
          content={formatMessage(messages.unsavedChanges)}
        />
      );
    } else {
      // readwrite / modified
      return (
        <Popup
          size="tiny"
          trigger={
            <Icon.Group>
              <Icon name="file outline" color="red" />
              <Icon corner="top right" name="edit" />
            </Icon.Group>
          }
          content={formatMessage(messages.unsavedChanges)}
        />
      );
    }
  }

  if (readWrite) {
    // readwrite / unmodified
    return (
      <Icon.Group>
        <Icon name="file outline" />
        <Icon corner="top right" name="edit" />
      </Icon.Group>
    );
  }

  // admin / unmodified
  return <Icon name="file outline" style={{ display: 'inline' }} />;
};

// Mirrors the LINKPlanner project export item in ExportSubmenu: export when no
// file exists, show progress while it is being built, then offer a download.
// Only mounted while its popup/modal is open, so the status is only polled then.
const ObsoleteExportButton = ({ projectId, projectName, ...buttonProps }) => {
  const { formatMessage } = useIntl();
  const query = useQuery(
    ['ReportMenu', projectId],
    async () => getWithAuth(`project/${projectId}/reports/available`),
    { retry: false, refetchInterval: 5000 }
  );
  const { status, reportId } = getExportReportStatus(query.data, 'lpp') || {};

  const createLabel = formatMessage({
    id: 'export.lpCreateLabel',
    defaultMessage: 'Export LINKPlanner Project File',
  });
  const downloadLabel = formatMessage({
    id: 'export.lpDownloadLabel',
    defaultMessage: 'Download Project File',
  });
  const progressLabel = formatMessage({
    id: 'export.kmzprogresslabel',
    defaultMessage: 'Processing LINKPlanner v5 File',
  });

  if (status === 'in_progress') {
    return (
      <Button
        {...buttonProps}
        disabled
        loading={false}
        icon={{ name: 'spinner', loading: true }}
        labelPosition="left"
        content={progressLabel}
      />
    );
  }

  if (status === 'ready') {
    return (
      <Button
        {...buttonProps}
        icon="download"
        labelPosition="left"
        content={downloadLabel}
        title={downloadLabel}
        onClick={() => downloadReport(projectId, reportId, projectName, 'lpp')}
      />
    );
  }

  return (
    <Button
      {...buttonProps}
      title={createLabel}
      onClick={() => exportProject(projectId, projectName, formatMessage)}
    >
      <img
        src={linkPlannerIcon}
        alt=""
        style={{
          height: '13px',
          marginRight: '0.5em',
          verticalAlign: 'middle',
          backgroundColor: 'white',
        }}
      />
      {createLabel}
    </Button>
  );
};

const ObsoleteProjectWarning = ({ projectId, projectName }) => {
  const { formatMessage } = useIntl();

  return (
    <Popup
      hoverable
      wide="very"
      position="bottom left"
      size="large"
      trigger={
        <Icon
          name="warning sign"
          color="red"
          aria-label={formatMessage(messages.obsoleteProjectHeader)}
          style={{ marginLeft: '0.5em', cursor: 'help' }}        />
      }
    >
      <Popup.Header>
        <Icon name="warning sign" color="red" />
        {formatMessage(messages.obsoleteProjectHeader)}
      </Popup.Header>
      <Popup.Content>
        <Message warning style={{ display: 'block' }}>
          {formatMessage(messages.obsoleteProjectWarning)}
        </Message>
        <ObsoleteExportButton
          color="blue"
          size="small"
          projectId={projectId}
          projectName={projectName}        />
      </Popup.Content>
    </Popup>
  );
};

const ObsoleteOpenDialog = ({
  open,
  projectId,
  projectName,
  onCancel,
  onOpen,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Modal size="small" open={open} onClose={onCancel}>
      <Modal.Header>
        <Icon name="warning sign" color="red" />
        {formatMessage(messages.obsoleteProjectHeader)}
      </Modal.Header>
      <Modal.Content>
        {formatMessage(messages.obsoleteProjectConfirm)}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onCancel}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button color="red" onClick={onOpen}>
          {formatMessage(messages.openAndRecalculate)}
        </Button>
        <ObsoleteExportButton
          color="blue"
          projectId={projectId}
          projectName={projectName}        />
      </Modal.Actions>
    </Modal>
  );
};

const ProjectDropDownMenu = ({
  id,
  name,
  readOnly,
  in_work,
  modified,
  onRenameProject,
  setError,
}) => {
  const { formatMessage } = useIntl();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [currDropdown, setCurrDropdown] = useState(null);
  const preventDiscard = useSelector((state) => state.mainFrame.preventDiscard);

  const onCloneProject = (id, name) => {
    return () => {
      runWithConfirmation({
        header: `Duplicate: ${name}`,
        onConfirm: () => {
          postWithAuth(`project/${id}/clone`, {})
            .then(() => {
              qc.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
              setError(null);
            })
            .catch((e) => {
              setError(e.detail);
            });
        },
      });
    };
  };

  const onDeleteProject = (id, name, modified, readOnly) => {
    return () => {
      let deleteFunc, header;
      // we don't want to show the delete message for a discard or a permission change
      let hardDelete = false;
      if (modified) {
        // discard changes (deletes in-work)
        deleteFunc = (id) => postWithAuth(`project/${id}/work`, {}, 'DELETE');
        header = `${formatMessage(messages.discardChanges)}: ${name}`;
      } else if (readOnly) {
        // remove user from project
        deleteFunc = (id) =>
          postWithAuth(`project/${id}/permissions`, {}, 'DELETE');
        header = `${formatMessage(messages.leaveProject)}: ${name}`;
      } else {
        // delete project
        deleteFunc = (id) => postWithAuth(`project/${id}`, {}, 'DELETE');
        header = `${formatMessage(messages.delete)}: ${name}`;
        hardDelete = true;
      }

      runWithConfirmation({
        header: header,
        onConfirm: () => {
          if (hardDelete) {
            dispatch(
              longTaskStarted({
                heading: 'Deleting project',
                message: 'This may take a while.',
              })
            );
          }
          deleteFunc(id)
            .then(() => {
              qc.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
            })
            .catch(console.error)
            .finally(() => {
              if (hardDelete) {
                dispatch(longTaskComplete());
              }
            });
        },
      });
    };
  };

  return (
    <ReportMenuContext.Provider value={setCurrDropdown}>
      <Dropdown
        icon="ellipsis horizontal"
        direction="left"
        open={currDropdown === id}
        onOpen={() => setCurrDropdown(id)}
        onBlur={() => setCurrDropdown(null)}
        onClose={() => setCurrDropdown(null)}
      >
        <Dropdown.Menu>
          <Dropdown.Item
            icon="edit outline"
            text={`${formatMessage(messages.rename)} ${name}`}
            onClick={onRenameProject(id, name)}
            disabled={readOnly}
          />
          <Dropdown.Item
            icon="copy outline"
            text={formatMessage(messages.duplicateProject)}
            onClick={onCloneProject(id, name)}
          />
          <Dropdown.Item
            icon={
              in_work && modified ? 'red history' : 'trash alternate outline'
            }
            text={
              in_work && modified
                ? formatMessage(messages.discardChanges)
                : readOnly
                  ? formatMessage(messages.leaveProject)
                  : formatMessage(messages.delete)
            }
            onClick={onDeleteProject(id, name, in_work, readOnly)}
            disabled={preventDiscard === id}
          />
          <ExportSubmenu
            projectId={id}
            projectName={name}
            isOpen={currDropdown === id}
          />
        </Dropdown.Menu>
      </Dropdown>
    </ReportMenuContext.Provider>
  );
};

const ProjectListItem = ({ projectRow, setError }) => {
  const { formatMessage } = useIntl();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [renaming, setRenaming] = useState(null);
  const [confirmObsolete, setConfirmObsolete] = useState(false);

  const today = new Date().toDateString();
  const locale =
    navigator.languages && navigator.languages.length
      ? navigator.languages[0]
      : navigator.language || 'en-GB';

  const { permission, last_accessed, project, in_work, users, has_obsolete } =
    projectRow;
  const { id, name, data, modified } = project;

  const { description = '' } = data || {};
  const readOnly = permission === 1;
  const readWrite = permission === 2;
  const admin = permission === 3;
  const permissionName = ['read', 'readwrite', 'admin'][permission - 1];
  const modifiedDate = new Date(Date.parse(last_accessed));
  const modifiedDateString =
    modifiedDate.toDateString() === today
      ? modifiedDate.toLocaleString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : modifiedDate.toLocaleString(locale, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });

  const openProject = (pid, projectName) => {
    dispatch(
      loadProject({
        projectId: pid,
        projectName,
      })
    );
    navigate('/');
  };

  const onLoadProject = (pid) => {
    return (e) => {
      e.preventDefault();
      if (has_obsolete) {
        setConfirmObsolete(true);
        return;
      }
      openProject(pid, e.target.name);
    };
  };

  const renameProject = (id, name) => {
    return () => {
      setRenaming({ id, name, origName: name });
    };
  };

  const onNameKey = (e) => {
    if (e.key === 'Enter') {
      saveNameChange();
    }
  };

  const saveNameChange = () => {
    const currentRenaming = renaming;
    setRenaming(null);
    if (currentRenaming && currentRenaming.name !== currentRenaming.origName) {
      const { name, id } = currentRenaming;
      postWithAuth(`project/${id}`, { name }, 'PATCH').then(() => {
        qc.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
      });
    }
  };

  const onNameChange = (e, { value }) => {
    setRenaming({ ...renaming, name: value });
  };

  const onNameBlur = () => {
    saveNameChange();
  };

  return (
    <List.Item key={id}>
      <List.Content floated="right">
        <ProjectDropDownMenu
          id={id}
          name={name}
          readOnly={readOnly}
          in_work={in_work}
          modified={modified}
          onRenameProject={renameProject}
          setError={setError}
        />
      </List.Content>
      <List.Content floated="right">
        <ShareDialog
          projectId={id}
          projectName={name}
          initialUserNames={users}
          isAdmin={admin}
        />
      </List.Content>
      <List.Content floated="right">{modifiedDateString}</List.Content>
      <List.Icon>
        <ProjectIcon
          readWrite={readWrite}
          in_work={in_work}
          modified={modified}
          admin={admin}
          readOnly={readOnly}
          permissionName={permissionName}
        />
      </List.Icon>
      <List.Content>
        <List.Header
          name={name}
          title={formatMessage(messages.openProject)}        >
          {renaming && renaming.id === id ? (
            <Input
              value={renaming.name}
              fluid
              autoFocus
              autoCapitalize="off"
              onBlur={onNameBlur}
              onChange={onNameChange}
              onKeyPress={onNameKey}
              maxLength={NAME_MAX_LENGTH}
            />
          ) : (
            <>
              <Button
                onClick={onLoadProject(id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  fontSize: '1em',
                  fontWeight: '700',
                  textAlign: 'left',
                  color: '#4183C4',
                }}
              >
                {name}
              </Button>
              {has_obsolete && (
                <ObsoleteProjectWarning projectId={id} projectName={name} />
              )}
              <ObsoleteOpenDialog
                open={confirmObsolete}
                projectId={id}
                projectName={name}
                onCancel={() => setConfirmObsolete(false)}
                onOpen={() => {
                  setConfirmObsolete(false);
                  openProject(id, name);
                }}
              />
            </>
          )}
        </List.Header>
        <List.Description>{description}</List.Description>
      </List.Content>
    </List.Item>
  );
};

export const ProjectListItems = ({ projectsToDisplay, setError }) => {
  return (
    <>
      {projectsToDisplay.map((project) => {
        return (
          <ProjectListItem
            key={project.project.id}
            projectRow={project}
            setError={setError}
          />
        );
      })}
    </>
  );
};
